import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import {
    startOfMonth,
    endOfMonth,
    startOfYear,
    addMonths,
    addYears,
    eachDayOfInterval,
    format,
    getDay,
} from 'date-fns';
import {
    FiCalendar,
    FiChevronLeft,
    FiChevronRight,
    FiFilter,
    FiSearch,
    FiX,
    FiAlertCircle,
} from 'react-icons/fi';
import { Home, PanelsTopLeft, PartyPopper } from 'lucide-react';
import { HiOutlineSquare3Stack3D } from 'react-icons/hi2';

import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../common/BackButton';
import Toast from '../common/Toast';
import TextTooltip from '../common/TextTooltip';
import AssignmentContextMenu from '../schedule/components/AssignmentContextMenu';
import EditAssignment from '../assignments/EditAssignment';
import FinalPricingDetails from './FinalPricingDetails';
import { useHolidays } from '../../hooks/useHolidays';
import {
    CALENDAR_DEPARTMENTS,
    getFinalPricingCalendarEntries,
    subscribeToFinalPricingCalendar,
    unsubscribeFromFinalPricingCalendar,
} from '../../lib/supabase/finalPricingCalendar';
import { isFinalPricingRequired } from '../../lib/supabase/finalPricingModes';

const VIEW_PERMISSION = 'view_finalpricing_page';
const ALL_DEPARTMENT_KEYS = CALENDAR_DEPARTMENTS.map(department => department.key);

const PRICING_STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'priced', label: 'Priced only' },
    { value: 'unpriced', label: 'Unpriced only' },
];

const SENT_STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'sent', label: 'Sent' },
    { value: 'not_sent', label: 'Not sent' },
];

const JOB_TYPE_OPTIONS = ['all', 'Design', 'Estimation', 'Design & Estimation', 'Timber'];

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const DEPT_META = {
    roof: {
        label: 'Roof',
        icon: Home,
        iconOn: 'text-orange-500',
        on: 'border-orange-200 bg-orange-50 text-orange-700',
        off: 'border-gray-200 bg-gray-50 text-gray-400',
    },
    wall: {
        label: 'Wall',
        icon: PanelsTopLeft,
        iconOn: 'text-blue-500',
        on: 'border-blue-200 bg-blue-50 text-blue-700',
        off: 'border-gray-200 bg-gray-50 text-gray-400',
    },
    floor: {
        label: 'Floor',
        icon: HiOutlineSquare3Stack3D,
        iconOn: 'text-emerald-500',
        on: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        off: 'border-gray-200 bg-gray-50 text-gray-400',
    },
};

const hasExactPermission = (userProfile, permission) =>
    Array.isArray(userProfile?.permissions) &&
    userProfile.permissions.includes(permission);

const loadPref = (key, fallback) => {
    try {
        const value = localStorage.getItem(key);
        return value !== null ? JSON.parse(value) : fallback;
    } catch {
        return fallback;
    }
};

const savePref = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Ignore localStorage errors.
    }
};

const formatMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';

    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
    }).format(amount);
};

// Manual final price wins when present; otherwise fall back to this
// department's quoting figure (designer-reviewed, else estimator's rough
// number) — same cascade as everywhere else in the app now, no mode
// branch. A legitimately-entered $0 manual price still counts as priced
// (existence, not amount, is what matters for `entry.pricing`).
const getEntryAmount = (entry) => (
    entry.pricing
        ? entry.pricing.final_price
        : entry.estimationTotals?.quoting?.byDepartment?.[entry.department]
);

// Priced *status* is gated on the company's
// final_pricing_required_for_quotation setting — same rule as
// isDepartmentQuotable, which the Final Pricings list and the Quotation list
// both use. When a manual final price is required, a department carrying
// only the quoting fallback is not priced here either, so the calendar's
// filter, ratio bars and counts can't disagree with those two pages.
// getEntryAmount above stays deliberately ungated: the displayed dollar
// figure is always the effective one, per this plan's convention.
const isEntryPriced = (entry, requireFinalPricing) => {
    if (entry.pricing) return true;
    if (requireFinalPricing) return false;
    return !!getEntryAmount(entry);
};

// Priced/unpriced ratio as a two-color hard-split "progress bar" background —
// emerald for the priced share, blue for the unpriced share. Returns null
// for an empty day so callers can fall back to their normal styling.
const getRatioGradientStyle = (entries, requireFinalPricing) => {
    const total = entries.length;
    if (total === 0) return null;

    const priced = entries.filter(entry => isEntryPriced(entry, requireFinalPricing)).length;
    const pricedPct = Math.round((priced / total) * 100);

    return {
        background: `linear-gradient(to right, #10b981 0%, #10b981 ${pricedPct}%, #3b82f6 ${pricedPct}%, #3b82f6 100%)`,
    };
};

const PermissionDenied = () => (
    <div className="min-h-[55vh] flex items-center justify-center">
        <div className="max-w-md w-full rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-red-800">No permission</h1>
            <p className="text-sm text-red-700 mt-2">
                You do not have permission to view the Final Pricing calendar.
            </p>
        </div>
    </div>
);

const CalendarEntryCard = React.memo(({ entry, requireFinalPricing, onOpen, onOpenAssignment, onEditAssignment }) => {
    const meta = DEPT_META[entry.department];
    const Icon = meta.icon;

    const amount = getEntryAmount(entry);
    const priced = isEntryPriced(entry, requireFinalPricing);

    const detailText = [
        entry.job.customer_name || 'No customer name',
        priced ? formatMoney(amount) : 'Not priced yet',
        entry.sent ? 'Quotation sent' : 'Quotation not sent',
        entry.job.is_cancelled ? 'CANCELLED' : null,
    ].filter(Boolean).join('  •  ');

    return (
        <TextTooltip text={detailText}>
            <AssignmentContextMenu
                assignment={entry.job}
                onOpenAssignment={onOpenAssignment}
                onEditAssignment={onEditAssignment}
                minimal
            >
                <button
                    type="button"
                    onClick={() => onOpen(entry.job.id)}
                    className={`pricing-calendar-entry w-full flex items-center gap-1.5 rounded-md border px-1.5 py-1 transition-colors text-left ${entry.job.is_cancelled
                        ? 'border-red-200 bg-red-50 hover:bg-red-100'
                        : 'border-gray-100 bg-gray-50 hover:bg-gray-100'
                        }`}
                >
                    <Icon className={`w-3 h-3 shrink-0 ${meta.iconOn}`} />
                    <span className="text-[11px] font-bold text-gray-800 truncate flex-1">{entry.job.unique_id || '—'}</span>
                    {priced ? (
                        <span className="text-[10px] font-bold text-gray-700 tabular-nums shrink-0">
                            {formatMoney(amount)}
                        </span>
                    ) : (
                        <span className="text-[9px] font-medium text-gray-400 shrink-0">—</span>
                    )}
                </button>
            </AssignmentContextMenu>
        </TextTooltip>
    );
});

const MonthDayCell = ({ day, entries, requireFinalPricing, onOpenEntry, onOpenAssignment, onEditAssignment, ratioColors, holidays = [] }) => {
    const ratioStyle = ratioColors ? getRatioGradientStyle(entries, requireFinalPricing) : null;
    const isHoliday = holidays.length > 0;

    return (
        <div className="pricing-calendar-day h-[240px] bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div
                className={`flex items-center justify-between px-3 py-2 border-b ${ratioStyle ? 'border-transparent' : isHoliday ? 'border-amber-200 bg-amber-50' : 'border-gray-100 bg-gray-50'}`}
                style={ratioStyle || undefined}
            >
                <div>
                    <div className={`text-xs font-bold uppercase ${ratioStyle ? 'text-white/80' : 'text-gray-400'}`}>{format(day, 'EEE')}</div>
                    <div className={`text-lg font-bold leading-none ${ratioStyle ? 'text-white' : 'text-gray-900'}`}>{format(day, 'd')}</div>
                    {isHoliday && (
                        <TextTooltip text={holidays.join(', ')}>
                            <div className={`flex items-center gap-1 mt-0.5 text-[10px] font-semibold truncate max-w-[110px] ${ratioStyle ? 'text-white/90' : 'text-amber-700'}`}>
                                <PartyPopper className="w-2.5 h-2.5 shrink-0" />
                                <span className="truncate">{holidays[0]}</span>
                            </div>
                        </TextTooltip>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {entries.length > 0 && (
                        <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${ratioStyle ? 'bg-white/25 text-white' : 'bg-white border border-gray-200 text-gray-500'}`}>
                            {entries.length}
                        </span>
                    )}
                    <FiCalendar className={ratioStyle ? 'text-white/70' : 'text-gray-300'} />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
                {entries.length === 0 ? (
                    <p className="text-xs text-gray-300 italic">No entries</p>
                ) : (
                    entries.map(entry => (
                        <CalendarEntryCard
                            key={entry.entryId}
                            entry={entry}
                            requireFinalPricing={requireFinalPricing}
                            onOpen={onOpenEntry}
                            onOpenAssignment={onOpenAssignment}
                            onEditAssignment={onEditAssignment}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

const MonthView = ({ selectedDate, entriesByDay, requireFinalPricing, onOpenEntry, onOpenAssignment, onEditAssignment, ratioColors, holidayMap }) => {
    const days = useMemo(() => eachDayOfInterval({
        start: startOfMonth(selectedDate),
        end: endOfMonth(selectedDate),
    }), [selectedDate]);

    return (
        <div className="pricing-calendar-month grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            {days.map(day => {
                const key = format(day, 'yyyy-MM-dd');
                return (
                    <MonthDayCell
                        key={key}
                        day={day}
                        entries={entriesByDay[key] || []}
                        requireFinalPricing={requireFinalPricing}
                        onOpenEntry={onOpenEntry}
                        onOpenAssignment={onOpenAssignment}
                        onEditAssignment={onEditAssignment}
                        ratioColors={ratioColors}
                        holidays={holidayMap.get(key) || []}
                    />
                );
            })}
        </div>
    );
};

const DAY_POPOVER_WIDTH = 176;

const DayCountsPopover = ({ day, entries, requireFinalPricing, onSelectDay, ratioColors, holidays = [] }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [triggerEl, setTriggerEl] = useState(null);
    const [pos, setPos] = useState(null);

    useEffect(() => {
        if (!isHovered || !triggerEl) {
            setPos(null);
            return;
        }

        const rect = triggerEl.getBoundingClientRect();
        const showAbove = rect.top > 140;
        const left = Math.min(
            Math.max(rect.left + rect.width / 2, DAY_POPOVER_WIDTH / 2 + 8),
            window.innerWidth - DAY_POPOVER_WIDTH / 2 - 8
        );

        setPos({
            top: showAbove ? rect.top - 8 : rect.bottom + 8,
            left,
            showAbove,
        });
    }, [isHovered, triggerEl]);

    const total = entries.length;
    const priced = entries.filter(entry => isEntryPriced(entry, requireFinalPricing)).length;
    const sent = entries.filter(entry => entry.sent).length;
    const ratioStyle = ratioColors && total > 0 ? getRatioGradientStyle(entries, requireFinalPricing) : null;
    const isHoliday = holidays.length > 0;

    return (
        <>
            <button
                ref={setTriggerEl}
                type="button"
                onClick={() => onSelectDay(day)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={ratioStyle || undefined}
                className={`aspect-square rounded-md text-[10px] font-semibold flex flex-col items-center justify-center transition-colors ${ratioStyle
                    ? 'text-white'
                    : total > 0
                        ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                        : isHoliday
                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                            : 'text-gray-400 hover:bg-gray-50'
                    } ${!ratioStyle && isHoliday ? 'ring-1 ring-inset ring-amber-300' : ''}`}
            >
                <span>{format(day, 'd')}</span>
                {total > 0 && (
                    <span className={`text-[8px] font-bold ${ratioStyle ? 'text-white/90' : 'text-blue-500'}`}>{total}</span>
                )}
            </button>

            {isHovered && pos && (total > 0 || isHoliday) && createPortal(
                <div
                    className="fixed z-[10000] bg-white border border-gray-200 rounded-xl shadow-xl p-3 pointer-events-none"
                    style={{
                        top: pos.top,
                        left: pos.left,
                        width: DAY_POPOVER_WIDTH,
                        transform: `translate(-50%, ${pos.showAbove ? '-100%' : '0'})`,
                    }}
                >
                    <p className="text-[10px] font-bold text-gray-800 mb-2">{format(day, 'EEE, MMM d')}</p>

                    {isHoliday && (
                        <p className="text-[10px] font-semibold text-amber-600 mb-2 flex items-center gap-1">
                            <PartyPopper className="w-3 h-3 shrink-0" />
                            <span>{holidays.join(', ')}</span>
                        </p>
                    )}

                    {total > 0 && (
                    <div className="space-y-1">
                        <div className="flex items-center justify-between text-[11px]">
                            <span className="text-gray-500">Total</span>
                            <span className="font-bold text-gray-800 tabular-nums">{total}</span>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                            <span className="text-gray-500">Priced</span>
                            <span className="font-bold text-emerald-600 tabular-nums">{priced}</span>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                            <span className="text-gray-500">Unpriced</span>
                            <span className="font-bold text-gray-400 tabular-nums">{total - priced}</span>
                        </div>
                        <div className="flex items-center justify-between text-[11px] pt-1 mt-1 border-t border-gray-100">
                            <span className="text-gray-500">Quotation sent</span>
                            <span className="font-bold text-blue-600 tabular-nums">{sent}/{total}</span>
                        </div>
                    </div>
                    )}
                </div>,
                document.body
            )}
        </>
    );
};

const MiniMonthCalendar = ({ monthDate, entriesByDay, requireFinalPricing, onSelectDay, onSelectMonth, ratioColors, holidayMap }) => {
    const days = useMemo(() => eachDayOfInterval({
        start: startOfMonth(monthDate),
        end: endOfMonth(monthDate),
    }), [monthDate]);

    const leadingBlanks = getDay(startOfMonth(monthDate));

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <button
                type="button"
                onClick={() => onSelectMonth(monthDate)}
                className="w-full px-3 py-2 border-b border-gray-100 bg-gray-50 text-sm font-bold text-gray-900 hover:bg-gray-100 text-left"
            >
                {format(monthDate, 'MMMM')}
            </button>

            <div className="p-2">
                <div className="grid grid-cols-7 gap-1 mb-1">
                    {WEEKDAY_LABELS.map((label, index) => (
                        <div key={index} className="text-center text-[9px] font-bold text-gray-300 uppercase">
                            {label}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: leadingBlanks }).map((_, index) => (
                        <div key={`blank-${index}`} />
                    ))}

                    {days.map(day => {
                        const key = format(day, 'yyyy-MM-dd');

                        return (
                            <DayCountsPopover
                                key={key}
                                day={day}
                                entries={entriesByDay[key] || []}
                                requireFinalPricing={requireFinalPricing}
                                onSelectDay={onSelectDay}
                                ratioColors={ratioColors}
                                holidays={holidayMap.get(key) || []}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const YearView = ({ selectedDate, entriesByDay, requireFinalPricing, onSelectDay, onSelectMonth, ratioColors, holidayMap }) => {
    const months = useMemo(() => (
        Array.from({ length: 12 }).map((_, index) => new Date(selectedDate.getFullYear(), index, 1))
    ), [selectedDate]);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {months.map(monthDate => (
                <MiniMonthCalendar
                    key={monthDate.toISOString()}
                    monthDate={monthDate}
                    entriesByDay={entriesByDay}
                    requireFinalPricing={requireFinalPricing}
                    onSelectDay={onSelectDay}
                    onSelectMonth={onSelectMonth}
                    ratioColors={ratioColors}
                    holidayMap={holidayMap}
                />
            ))}
        </div>
    );
};

const FilterBar = ({
    search, setSearch,
    departments, toggleDepartment,
    jobType, setJobType,
    cancelledStatus, setCancelledStatus,
    pricingStatus, setPricingStatus,
    sentStatus, setSentStatus,
    minPrice, setMinPrice,
    maxPrice, setMaxPrice,
    onClearAll,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };

        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const activeCount = [
        jobType !== 'all',
        cancelledStatus !== 'active',
        pricingStatus !== 'all',
        sentStatus !== 'all',
        minPrice !== '',
        maxPrice !== '',
    ].filter(Boolean).length;

    return (
        <div className="pricing-calendar-filters flex flex-wrap items-center gap-2 px-3 py-2 bg-white border-b border-gray-100">
            <div className="relative min-w-[160px] max-w-sm flex-1">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search job ID, customer or address…"
                    className="w-full h-8 pl-8 pr-8 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <FiX className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            <div className="flex items-center h-8 rounded-md border border-gray-200 overflow-hidden">
                {CALENDAR_DEPARTMENTS.map(department => {
                    const meta = DEPT_META[department.key];
                    const Icon = meta.icon;
                    const active = departments.includes(department.key);

                    return (
                        <button
                            key={department.key}
                            type="button"
                            onClick={() => toggleDepartment(department.key)}
                            className={`flex items-center gap-1.5 h-full px-2.5 text-xs font-medium transition-colors border-l border-gray-200 first:border-l-0 ${active ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <Icon className={`w-3 h-3 ${active ? 'text-white' : meta.iconOn}`} />
                            {department.label}
                        </button>
                    );
                })}
            </div>

            <div className="relative" ref={ref}>
                <button
                    type="button"
                    onClick={() => setOpen(value => !value)}
                    className={`flex items-center gap-1.5 h-8 px-3 text-xs font-medium border rounded-md transition-all ${activeCount > 0 || open
                        ? 'bg-blue-50 border-blue-400 text-blue-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-blue-400'
                        }`}
                >
                    <FiFilter className="w-3 h-3" />
                    Filters
                    {activeCount > 0 && (
                        <span className="bg-blue-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                            {activeCount}
                        </span>
                    )}
                </button>

                {open && (
                    <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-3 space-y-4">
                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Pricing status</p>
                            <div className="flex rounded-md border border-gray-200 overflow-hidden">
                                {PRICING_STATUS_OPTIONS.map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setPricingStatus(option.value)}
                                        className={`flex-1 px-2 py-1.5 text-[11px] font-medium border-l border-gray-200 first:border-l-0 ${pricingStatus === option.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Quotation sent</p>
                            <div className="flex rounded-md border border-gray-200 overflow-hidden">
                                {SENT_STATUS_OPTIONS.map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setSentStatus(option.value)}
                                        className={`flex-1 px-2 py-1.5 text-[11px] font-medium border-l border-gray-200 first:border-l-0 ${sentStatus === option.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Job type</p>
                            <select
                                value={jobType}
                                onChange={(event) => setJobType(event.target.value)}
                                className="w-full h-8 rounded-md border border-gray-200 px-2 text-xs"
                            >
                                {JOB_TYPE_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option === 'all' ? 'All job types' : option}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cancelled jobs</p>
                            <select
                                value={cancelledStatus}
                                onChange={(event) => setCancelledStatus(event.target.value)}
                                className="w-full h-8 rounded-md border border-gray-200 px-2 text-xs"
                            >
                                <option value="active">Active only</option>
                                <option value="all">Active and cancelled</option>
                                <option value="cancelled">Cancelled only</option>
                            </select>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Final price range</p>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    value={minPrice}
                                    onChange={(event) => setMinPrice(event.target.value)}
                                    placeholder="Min"
                                    className="w-full h-8 rounded-md border border-gray-200 px-2 text-xs"
                                />
                                <span className="text-gray-300">–</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={maxPrice}
                                    onChange={(event) => setMaxPrice(event.target.value)}
                                    placeholder="Max"
                                    className="w-full h-8 rounded-md border border-gray-200 px-2 text-xs"
                                />
                            </div>
                        </div>

                        {activeCount > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    onClearAll();
                                    setOpen(false);
                                }}
                                className="w-full py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200 font-medium"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const FinalPricingCalendar = () => {
    const { company, userProfile } = useAuth();
    const { companySlug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const canView = hasExactPermission(userProfile, VIEW_PERMISSION);
    const requireFinalPricing = isFinalPricingRequired(company);
    const holidayMap = useHolidays(company?.state);

    const [viewMode, setViewMode] = useState(() => loadPref('fpc_view_mode', 'month'));
    const [selectedDate, setSelectedDate] = useState(() => {
        const stored = loadPref('fpc_selected_date', null);
        const parsed = stored ? new Date(stored) : new Date();
        return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    });

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [departments, setDepartments] = useState(() => loadPref('fpc_departments', ALL_DEPARTMENT_KEYS));
    const [jobType, setJobType] = useState(() => loadPref('fpc_job_type', 'all'));
    const [cancelledStatus, setCancelledStatus] = useState(() => loadPref('fpc_cancelled', 'active'));
    const [pricingStatus, setPricingStatus] = useState(() => loadPref('fpc_pricing_status', 'all'));
    const [sentStatus, setSentStatus] = useState(() => loadPref('fpc_sent_status', 'all'));
    const [minPrice, setMinPrice] = useState(() => loadPref('fpc_min_price', ''));
    const [maxPrice, setMaxPrice] = useState(() => loadPref('fpc_max_price', ''));

    const [selectedFinalPricingJobId, setSelectedFinalPricingJobId] = useState(null);
    const [editAssignmentJobId, setEditAssignmentJobId] = useState(null);

    const [monthRatioColors, setMonthRatioColors] = useState(() => loadPref('fpc_month_ratio_colors', true));
    const [yearRatioColors, setYearRatioColors] = useState(() => loadPref('fpc_year_ratio_colors', true));

    useEffect(() => savePref('fpc_view_mode', viewMode), [viewMode]);
    useEffect(() => savePref('fpc_selected_date', format(selectedDate, 'yyyy-MM-dd')), [selectedDate]);
    useEffect(() => savePref('fpc_departments', departments), [departments]);
    useEffect(() => savePref('fpc_job_type', jobType), [jobType]);
    useEffect(() => savePref('fpc_cancelled', cancelledStatus), [cancelledStatus]);
    useEffect(() => savePref('fpc_pricing_status', pricingStatus), [pricingStatus]);
    useEffect(() => savePref('fpc_sent_status', sentStatus), [sentStatus]);
    useEffect(() => savePref('fpc_min_price', minPrice), [minPrice]);
    useEffect(() => savePref('fpc_max_price', maxPrice), [maxPrice]);
    useEffect(() => savePref('fpc_month_ratio_colors', monthRatioColors), [monthRatioColors]);
    useEffect(() => savePref('fpc_year_ratio_colors', yearRatioColors), [yearRatioColors]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const rangeBounds = useMemo(() => {
        const start = viewMode === 'year' ? startOfYear(selectedDate) : startOfMonth(selectedDate);
        const end = viewMode === 'year' ? startOfYear(addYears(selectedDate, 1)) : startOfMonth(addMonths(selectedDate, 1));
        return { start, end };
    }, [viewMode, selectedDate]);

    const isDateInRange = useCallback((date) => {
        if (!date) return false;
        return date >= rangeBounds.start && date < rangeBounds.end;
    }, [rangeBounds]);

    const fetchEntries = useCallback(async () => {
        if (!canView || !company?.id) return;

        setLoading(true);
        try {
            const result = await getFinalPricingCalendarEntries(company.id, {
                rangeStart: format(rangeBounds.start, 'yyyy-MM-dd'),
                rangeEnd: format(rangeBounds.end, 'yyyy-MM-dd'),
                departments,
                jobType,
                cancelledStatus,
            });

            setEntries(result.data);
        } catch (error) {
            console.error('Failed to load final pricing calendar entries:', error);
            Toast.error('Failed to load final pricing calendar', { position: 'bottom-right' });
        } finally {
            setLoading(false);
        }
    }, [canView, company?.id, rangeBounds, departments, jobType, cancelledStatus]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const handlePricingChange = useCallback((payload) => {
        const isDelete = payload.eventType === 'DELETE';
        const row = isDelete ? payload.old : payload.new;
        if (!row) return;

        // A job can carry more than one entry for the same department (see
        // finalPricingCalendar.js), and they all share the same final price,
        // so every matching entry needs the patch — not just the first found.
        setEntries(prev => prev.map(entry => (
            entry.job.id === row.job_id && entry.department === row.department
                ? { ...entry, pricing: isDelete ? null : payload.new }
                : entry
        )));
    }, []);

    const handleQuoteChange = useCallback((payload) => {
        const updated = payload.new;
        if (!updated) return;

        setEntries(prev => {
            const idx = prev.findIndex(entry => entry.quoteId === updated.id);
            if (idx === -1) return prev;

            const dateValue = updated.expected_delivery_date || updated.expected_finish_date;
            const nextDate = dateValue ? new Date(dateValue) : null;
            const isPlayground = updated.quote_playground === true || updated.delivery_playground === true;

            if (!updated.is_required || isPlayground || !isDateInRange(nextDate)) {
                return prev.filter(entry => entry.quoteId !== updated.id);
            }

            const next = [...prev];
            next[idx] = { ...next[idx], date: nextDate };
            return next;
        });
    }, [isDateInRange]);

    const handleJobChange = useCallback((payload) => {
        const updated = payload.new;
        if (!updated) return;

        setEntries(prev => prev.map(entry => {
            if (entry.job.id !== updated.id) return entry;

            return {
                ...entry,
                job: {
                    ...entry.job,
                    unique_id: updated.unique_id ?? entry.job.unique_id,
                    customer_name: updated.customer_name ?? entry.job.customer_name,
                    address: updated.customer_address ?? entry.job.address,
                    is_cancelled: !!updated.is_cancelled,
                },
            };
        }));
    }, []);

    const handleConversationChange = useCallback((payload) => {
        const row = payload.new;
        if (!row || row.conversation_type !== 'quotation_email' || row.direction !== 'outbound' || row.status !== 'sent') return;

        const quotedDepartments = row.metadata?.quoted_departments;
        if (!Array.isArray(quotedDepartments) || quotedDepartments.length === 0) return;

        const departmentKeys = new Set(quotedDepartments.map(item => item.department).filter(Boolean));

        setEntries(prev => prev.map(entry => {
            if (entry.job.id !== row.job_id || !departmentKeys.has(entry.department)) return entry;
            return { ...entry, sent: true };
        }));
    }, []);

    useEffect(() => {
        if (!canView || !company?.id) return undefined;

        const channels = subscribeToFinalPricingCalendar(company.id, {
            onPricingChange: handlePricingChange,
            onQuoteChange: handleQuoteChange,
            onJobChange: handleJobChange,
            onConversationChange: handleConversationChange,
        });

        return () => {
            unsubscribeFromFinalPricingCalendar(channels);
        };
    }, [canView, company?.id, handlePricingChange, handleQuoteChange, handleJobChange, handleConversationChange]);

    const displayEntries = useMemo(() => {
        const minPriceValue = minPrice === '' ? null : Number(minPrice);
        const maxPriceValue = maxPrice === '' ? null : Number(maxPrice);
        const term = debouncedSearch.trim().toLowerCase();

        return entries.filter(entry => {
            const priced = isEntryPriced(entry, requireFinalPricing);

            if (pricingStatus === 'priced' && !priced) return false;
            if (pricingStatus === 'unpriced' && priced) return false;

            if (priced && (minPriceValue !== null || maxPriceValue !== null)) {
                const amount = Number(getEntryAmount(entry));
                if (minPriceValue !== null && amount < minPriceValue) return false;
                if (maxPriceValue !== null && amount > maxPriceValue) return false;
            }

            if (sentStatus === 'sent' && !entry.sent) return false;
            if (sentStatus === 'not_sent' && entry.sent) return false;

            if (term) {
                const haystack = `${entry.job.unique_id || ''} ${entry.job.customer_name || ''} ${entry.job.address || ''}`.toLowerCase();
                if (!haystack.includes(term)) return false;
            }

            return true;
        });
    }, [entries, requireFinalPricing, pricingStatus, minPrice, maxPrice, sentStatus, debouncedSearch]);

    const displayStats = useMemo(() => {
        const jobIds = new Set(displayEntries.map(entry => entry.job.id));
        return { jobCount: jobIds.size, entryCount: displayEntries.length };
    }, [displayEntries]);

    const entriesByDay = useMemo(() => {
        const map = {};
        displayEntries.forEach(entry => {
            if (!entry.date) return;
            const key = format(entry.date, 'yyyy-MM-dd');
            if (!map[key]) map[key] = [];
            map[key].push(entry);
        });
        return map;
    }, [displayEntries]);

    const toggleDepartment = (key) => {
        setDepartments(prev => (
            prev.includes(key)
                ? prev.filter(item => item !== key)
                : [...prev, key]
        ));
    };

    const clearAllFilters = () => {
        setJobType('all');
        setCancelledStatus('active');
        setPricingStatus('all');
        setSentStatus('all');
        setMinPrice('');
        setMaxPrice('');
    };

    const goToPrevious = () => {
        setSelectedDate(prev => (viewMode === 'year' ? addYears(prev, -1) : addMonths(prev, -1)));
    };

    const goToNext = () => {
        setSelectedDate(prev => (viewMode === 'year' ? addYears(prev, 1) : addMonths(prev, 1)));
    };

    const handleSelectDay = (day) => {
        setSelectedDate(startOfMonth(day));
        setViewMode('month');
    };

    const openAssignment = useCallback((jobId) => {
        window.open(`/${companySlug}/assignment/${jobId}`, '_blank');
    }, [companySlug]);

    const editAssignment = useCallback((jobId) => {
        setEditAssignmentJobId(jobId);
    }, []);

    const handleSelectMonth = (monthDate) => {
        setSelectedDate(startOfMonth(monthDate));
        setViewMode('month');
    };

    if (!canView) return <PermissionDenied />;

    return (
        <div className="pricing-calendar-page flex flex-col gap-6 pb-10">
            <div className="flex flex-col gap-3">
                <BackButton
                    text="Back"
                    variant="ghost"
                    size="sm"
                    iconType="chevron"
                    className="text-blue-600 hover:text-blue-800 self-start"
                />

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                            <FiCalendar className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Final Pricing Calendar</h1>
                            <p className="text-gray-500 text-sm mt-0.5">
                                Final pricing laid out by delivery date.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate(`/${companySlug}/final-pricings`, { state: { from: location.pathname } })}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                        Table view
                    </button>
                </div>
            </div>

            <div className="pricing-calendar-panel flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                <div className="pricing-calendar-navigation flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
                    <div className="flex items-center h-8 rounded-md border border-gray-200 overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setViewMode('month')}
                            className={`h-full px-3 text-xs font-medium transition-colors ${viewMode === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >
                            Month
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode('year')}
                            className={`h-full px-3 text-xs font-medium transition-colors border-l border-gray-200 ${viewMode === 'year' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >
                            Year
                        </button>
                    </div>

                    <div className="flex items-center gap-1.5">
                        <span className="hidden sm:inline text-[11px] font-semibold text-gray-400 uppercase tracking-wide mr-0.5">
                            Ratio colors
                        </span>
                        <button
                            type="button"
                            onClick={() => setMonthRatioColors(value => !value)}
                            title="Toggle priced-ratio colors in Month view"
                            className={`h-8 px-2.5 rounded-md text-xs font-medium border transition-colors ${monthRatioColors
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                                }`}
                        >
                            Month
                        </button>
                        <button
                            type="button"
                            onClick={() => setYearRatioColors(value => !value)}
                            title="Toggle priced-ratio colors in Year view"
                            className={`h-8 px-2.5 rounded-md text-xs font-medium border transition-colors ${yearRatioColors
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                                }`}
                        >
                            Year
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={goToPrevious}
                            aria-label="Previous period"
                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                        >
                            <FiChevronLeft />
                        </button>

                        <DatePicker
                            selected={selectedDate}
                            onChange={(date) => setSelectedDate(viewMode === 'year' ? startOfYear(date) : startOfMonth(date))}
                            dateFormat={viewMode === 'year' ? 'yyyy' : 'MMMM yyyy'}
                            showMonthYearPicker={viewMode === 'month'}
                            showYearPicker={viewMode === 'year'}
                            className="w-32 text-center border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold"
                        />

                        <button
                            type="button"
                            onClick={goToNext}
                            aria-label="Next period"
                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                        >
                            <FiChevronRight />
                        </button>
                    </div>
                </div>

                <FilterBar
                    search={search}
                    setSearch={setSearch}
                    departments={departments}
                    toggleDepartment={toggleDepartment}
                    jobType={jobType}
                    setJobType={setJobType}
                    cancelledStatus={cancelledStatus}
                    setCancelledStatus={setCancelledStatus}
                    pricingStatus={pricingStatus}
                    setPricingStatus={setPricingStatus}
                    sentStatus={sentStatus}
                    setSentStatus={setSentStatus}
                    minPrice={minPrice}
                    setMinPrice={setMinPrice}
                    maxPrice={maxPrice}
                    setMaxPrice={setMaxPrice}
                    onClearAll={clearAllFilters}
                />

                <div className="px-3 sm:px-4 py-1.5 text-[11px] font-medium text-gray-500 border-b border-gray-100">
                    {loading ? (
                        'Loading…'
                    ) : (
                        <>
                            {displayStats.jobCount} job{displayStats.jobCount === 1 ? '' : 's'}
                            {displayStats.entryCount !== displayStats.jobCount && (
                                <> &middot; {displayStats.entryCount} entries</>
                            )} shown
                        </>
                    )}
                </div>

                <div className="p-3 sm:p-4">
                    {loading ? (
                        <div className="h-40 flex items-center justify-center">
                            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            <span className="ml-3 text-sm text-gray-500">Loading…</span>
                        </div>
                    ) : viewMode === 'month' ? (
                        <MonthView
                            selectedDate={selectedDate}
                            entriesByDay={entriesByDay}
                            requireFinalPricing={requireFinalPricing}
                            onOpenEntry={setSelectedFinalPricingJobId}
                            onOpenAssignment={openAssignment}
                            onEditAssignment={editAssignment}
                            ratioColors={monthRatioColors}
                            holidayMap={holidayMap}
                        />
                    ) : (
                        <YearView
                            selectedDate={selectedDate}
                            entriesByDay={entriesByDay}
                            requireFinalPricing={requireFinalPricing}
                            onSelectDay={handleSelectDay}
                            onSelectMonth={handleSelectMonth}
                            ratioColors={yearRatioColors}
                            holidayMap={holidayMap}
                        />
                    )}
                </div>
            </div>

            {selectedFinalPricingJobId && (
                <FinalPricingDetails
                    modalJobId={selectedFinalPricingJobId}
                    modalCompanySlug={companySlug}
                    onClose={() => setSelectedFinalPricingJobId(null)}
                    onSaved={({ jobId, department, finalPricing }) => {
                        setEntries(prev => prev.map(entry =>
                            entry.job.id === jobId && entry.department === department
                                ? { ...entry, pricing: finalPricing || null }
                                : entry
                        ));
                    }}
                />
            )}

            {editAssignmentJobId && (
                <EditAssignment
                    modalJobId={editAssignmentJobId}
                    modalCompanySlug={companySlug}
                    onClose={() => setEditAssignmentJobId(null)}
                />
            )}
        </div>
    );
};

export default FinalPricingCalendar;
